import { Message, WarnMessage } from "../model/Message";
import { yammerChannelMessage, messageServiceMessage } from "./WelcomeMessages";

interface Configuration {
  Name: string
  AppRegistrationAppId: string
  SmartAPI: string
  SmartAPIScope: string
  SolarGisPvSpotAPI: string
  DowntimeAPI: (marketKey: string) => string
  DowntimeAPIScope: (marketKey: string) => string
  DowntimeAPIScopes: () => Array<string>
  ExceptionAPI: string
  ApplicationInsightsKey: string
  ScadaAnalysisLauncher: string
  SmartApplications: {
    GroupName: string
    Applications: {
      Name: string
      Url: string
    }[]
  }[],
  WelcomeMessages: {
    Storage: Message[],
    Session: Message[]
  }
}

var smartApplications = [
  {
    GroupName: 'SMART View',
    Applications: [{ Name: 'SMART View', Url: 'https://smartview.res-smart.com/' }]
  },
  {
    GroupName: 'SMART MVC',
    Applications: [
      { Name: 'SMART UKI', Url: 'https://smart-uki.res-smart.com/' },
      { Name: 'SMART France', Url: 'https://smart-france.res-smart.com/' },
      { Name: 'SMART Americas', Url: 'http://smart-americas/' },
      { Name: 'SMART Americas (Azure)', Url: 'https://smart-americas.res-smart.com/' },
      { Name: 'SMART NV', Url: 'https://smart-nordics.res-smart.com/' },
      { Name: 'SMART Australia', Url: 'https://smart-australia.res-smart.com/' }
    ]
  },
  {
    GroupName: 'Other',
    Applications: [
      { Name: 'SMART Storage', Url: 'http://storage.smart.res.lan/' },
      { Name: 'Curtailment', Url: 'http://curtailment.smart.res.lan/' }
    ]
  }
]

const environments = {
  DEVELOPMENT: "development",
  STAGING: "staging",
  PRODUCTION: "production"
}

const downtimeApiScopes = {
  [environments.DEVELOPMENT]: {
    france: "58673cc0-757d-4eef-a31c-21fabcf5b5c9/user_impersonation",
    nv: "27187611-bd7f-4995-96fa-8ed20a267f7a/user_impersonation",
    aus: "0bde67a5-fda2-4b3b-8b27-86c67957aa4d/user_impersonation",
    uki: "def61dec-8e45-4228-98c8-2f3e52b62b71/user_impersonation",
    americasazure: "f6794ed4-fc47-4248-9ae3-2d5b2c3695f1/user_impersonation",
  },
  [environments.STAGING]: {
    france: "58673cc0-757d-4eef-a31c-21fabcf5b5c9/user_impersonation",
    nv: "27187611-bd7f-4995-96fa-8ed20a267f7a/user_impersonation",
    aus: "0bde67a5-fda2-4b3b-8b27-86c67957aa4d/user_impersonation",
    uki: "def61dec-8e45-4228-98c8-2f3e52b62b71/user_impersonation",
    americasazure: "f6794ed4-fc47-4248-9ae3-2d5b2c3695f1/user_impersonation",
  },
  [environments.PRODUCTION]: {
    france: "f7b18faf-3750-4496-b6e4-bd032265995a/user_impersonation",
    nv: "27187611-bd7f-4995-96fa-8ed20a267f7a/user_impersonation",
    aus: "0bde67a5-fda2-4b3b-8b27-86c67957aa4d/user_impersonation",
    uki: "f886c059-877a-4f1c-b4a4-f60edd8af94d/user_impersonation",
    americasazure: "f6794ed4-fc47-4248-9ae3-2d5b2c3695f1/user_impersonation",
  }
}

const developmentConfiguration: Configuration = {
  Name: 'DEVELOPMENT',
  AppRegistrationAppId: "bd387753-4fcc-4f79-ba49-da1e82892e92",
  // SmartAPI: 'https://test-api.smart.res.lan/api/v1/',
  SmartAPI: 'https://api.res-smart.com/api/v1/',
  SmartAPIScope: 'fe19124b-940f-4d18-a3fb-363872597acc/user_impersonation',
  // SmartAPI: 'https://localhost:44365/api/v1/',
  ExceptionAPI: '',
  ApplicationInsightsKey: '',
  // DowntimeAPI: (marketKey: string) => `https://downtime.smart-${marketKey}.res.lan/api/v1/`,
  // DowntimeAPI: (_: string) => 'https://localhost:44334/api/v1/',
  // DowntimeAPI: (_: string) => 'https://test-downtime.smart-uki.res.lan/api/v1/',
  DowntimeAPI: (marketKey: string) => {
     switch(marketKey.toLowerCase().trim()) {
      case "france" : return `https://as-downtime-api-staging-france.azurewebsites.net/api/v1/`;
      case "nv" : return `https://as-downtime-api-production-nordics.azurewebsites.net/api/v1/`;
      case "aus" : return `https://as-downtime-api-production-australia.azurewebsites.net/api/v1/`;
      case "uki" : return `https://as-downtime-api-staging-uki.azurewebsites.net/api/v1/`;
      case "americasazure" : return `https://as-downtime-api-production-americas.azurewebsites.net/api/v1/`;
      default: return `https://downtime.smart-${marketKey}.res.lan/api/v1/`;
     }
  },
  DowntimeAPIScope: (marketKey: string) => downtimeApiScopes.development[marketKey.toLowerCase().trim()],
  DowntimeAPIScopes: () => getAllDowntimeScopesFor(environments.DEVELOPMENT),
  SolarGisPvSpotAPI: 'https://res-solargis-pvspot.azurewebsites.net/',
  ScadaAnalysisLauncher: 'Launch.ScadaAnalysisTest',
  SmartApplications: smartApplications,
  WelcomeMessages: {
    Session: [ WarnMessage("Development", "You are viewing the development configuration") ],
    Storage: [ messageServiceMessage, yammerChannelMessage ]
  }
}

var Deployments: { Urls: RegExp[]; Config: Configuration }[] = [
  {
    // https can only have one '.' before smart.res.lan because the certificate is *.smart.res.lan and the * cannot match a '.'
    Urls: [
      /https:\/\/smart2.smart.res.lan\//,
      /https:\/\/test-smart2.smart.res.lan\//,
      /https:\/\/smart2.res-smart.com\//,
      /https:\/\/test-smart2.res-smart.com\//,
    ],
    Config: {
      Name: 'LIVE',
      AppRegistrationAppId: 'c2ab28fd-b2cd-406c-8cfe-30fd48f747e2',
      SmartAPI: 'https://api.res-smart.com/api/v1/',
      SmartAPIScope: 'fe19124b-940f-4d18-a3fb-363872597acc/user_impersonation',
      ExceptionAPI: 'https://exceptions.smart.res.lan/api/errors/',
      ApplicationInsightsKey: '429103c8-5736-4053-bc35-963060941d84',
      DowntimeAPI: (marketKey: string) => {
        switch(marketKey.toLowerCase().trim()) {
          case "france" : return `https://as-downtime-api-production-france.azurewebsites.net/api/v1/`;
          case "nv" : return `https://as-downtime-api-production-nordics.azurewebsites.net/api/v1/`;
          case "aus" : return `https://as-downtime-api-production-australia.azurewebsites.net/api/v1/`;
          case "uki" : return `https://as-downtime-api-production-uki.azurewebsites.net/api/v1/`;
          case "americasazure" : return `https://as-downtime-api-production-americas.azurewebsites.net/api/v1/`;
          default: return `https://downtime.smart-${marketKey}.res.lan/api/v1/`;
         }
      },
      DowntimeAPIScope: (marketKey: string) => downtimeApiScopes.production[marketKey.toLowerCase().trim()],
      DowntimeAPIScopes: () => getAllDowntimeScopesFor(environments.PRODUCTION),
      SolarGisPvSpotAPI: 'https://res-solargis-pvspot.azurewebsites.net/',
      ScadaAnalysisLauncher: 'Launch.ScadaAnalysis',
      SmartApplications: smartApplications,
      WelcomeMessages: {
        Session: [],
        Storage: [ messageServiceMessage, yammerChannelMessage ]
      }
    }
  },
  {
    // https can only have one '.' before smart.res.lan because the certificate is *.smart.res.lan and the * cannot match a '.'
    Urls: [
      /https:\/\/staging-smart2.smart.res.lan\//,
      /https:\/\/smart2webstaging.z6.web.core.windows.net\//,
    ],
    Config: {
      Name: 'STAGING',
      AppRegistrationAppId: "bd387753-4fcc-4f79-ba49-da1e82892e92",
      SmartAPI: 'https://api.res-smart.com/api/v1/',
      SmartAPIScope: 'fe19124b-940f-4d18-a3fb-363872597acc/user_impersonation',
      ExceptionAPI: '',
      ApplicationInsightsKey: "5d2959e0-4582-454b-a72b-ce5c6dbc5334",
      DowntimeAPI: (marketKey: string) => {
        switch(marketKey.toLowerCase().trim()) {
          case "france" : return `https://as-downtime-api-staging-france.azurewebsites.net/api/v1/`;
          case "nv" : return `https://as-downtime-api-production-nordics.azurewebsites.net/api/v1/`;
          case "aus" : return `https://as-downtime-api-production-australia.azurewebsites.net/api/v1/`;
          case "uki" : return `https://as-downtime-api-staging-uki.azurewebsites.net/api/v1/`;
          case "americasazure" : return `https://as-downtime-api-production-americas.azurewebsites.net/api/v1/`;
          default: return `https://test-downtime.smart-${marketKey}.res.lan/api/v1/`;
         }
      },
      DowntimeAPIScope: (marketKey: string) => downtimeApiScopes.staging[marketKey.toLowerCase().trim()],
      DowntimeAPIScopes: () => getAllDowntimeScopesFor(environments.STAGING),
      SolarGisPvSpotAPI: 'https://res-solargis-pvspot.azurewebsites.net/',
      ScadaAnalysisLauncher: 'Launch.ScadaAnalysisTest',
      SmartApplications: smartApplications,
      WelcomeMessages: {
        Session: [ WarnMessage("Staging", "You are viewing the staging configuration, this points to test APIs") ],
        Storage: [ messageServiceMessage, yammerChannelMessage ]
      }
    }
  },
  {
    Urls: [/http:\/\/localhost:3000\//, /https:\/\/localhost:3000\//],
    Config: developmentConfiguration
  },
  {
    Urls: [/http:\/\/localhost\//, /about:blank/],
    Config: {
      Name: 'JEST',
      AppRegistrationAppId: '',
      SmartAPI: 'http://fake.host/path/to/api/',
      SmartAPIScope: '',
      ExceptionAPI: '',
      ApplicationInsightsKey: '',
      DowntimeAPI: (_: string) => 'http://fake.host/path/to/api/',
      DowntimeAPIScope: (_: string) => '',
      DowntimeAPIScopes: () => [],
      SolarGisPvSpotAPI: '',
      ScadaAnalysisLauncher: 'Launch.ScadaAnalysisTest',
      SmartApplications: smartApplications,
      WelcomeMessages: {
        Session: [],
        Storage: []
      }
    }
  }
]

const getAllDowntimeScopesFor = (environmentName: string): Array<string> => {
  // For each key (market name) for an environment, return all the scopes
  const environmentSpecificDowntimeApiScopes = downtimeApiScopes[environmentName];
  return Object.keys(environmentSpecificDowntimeApiScopes).map(market => environmentSpecificDowntimeApiScopes[market]);
}

export function getConfiguration(deploymentUrl: string): Configuration {
  var deployment = Deployments.find(
    deploy => deploy.Urls.find(url => url.test(deploymentUrl)) !== undefined
  )
  if (deployment !== undefined) {
    return deployment.Config
  } else {
    return developmentConfiguration
  }
}

export const configuration: Configuration = getConfiguration(window.location.toString())
